import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrencyInput, parseCurrencyInput } from "@/lib/leadMeta";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { BookMarked, Plus, Loader2, Trash2, Pencil, Users, Phone, MapPin, Hash, Building2, Check, X, Cake, Download, FileText, CalendarClock, Upload, DollarSign, Zap, BarChart3, ListChecks } from "lucide-react";

const fmtDob = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 8);
  if (d.length < 3) return d;
  if (d.length < 5) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};
const fmtPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const displayPhone = (digits) => (digits ? fmtPhone(digits) : "");
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const daysUntil = (mdy) => {
  const p = (mdy || "").split("/");
  if (p.length !== 3) return null;
  const d = new Date(Number(p[2]), Number(p[0]) - 1, Number(p[1]));
  if (isNaN(d)) return null;
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
};

const EMPTY = { carrier: "", insured_name: "", address: "", address2: "", dob: "", phone: "", policy_number: "", renewal_date: "", premium: "", members: [] };

const CSV_FIELDS = [
  { key: "insured_name", label: "Insured name", required: true },
  { key: "carrier", label: "Carrier" },
  { key: "policy_number", label: "Policy number" },
  { key: "premium", label: "Annual premium" },
  { key: "dob", label: "Date of birth" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "renewal_date", label: "Renewal date" },
];
const CSV_ALIASES = {
  insured_name: ["insured name", "insured", "name"], carrier: ["carrier", "company"],
  policy_number: ["policy number", "policy #", "policy", "policy_number"], premium: ["premium", "annual premium"],
  dob: ["date of birth", "dob"], phone: ["phone", "telephone"], address: ["address"],
  renewal_date: ["renewal date", "renewal", "expiry", "renewal_date"],
};
const splitCSV = (text) => {
  const parseLine = (line) => {
    const out = []; let cur = ""; let q = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
      else if (ch === '"') q = true;
      else if (ch === ",") { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur); return out;
  };
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  return { headers: parseLine(lines[0]).map((h) => h.trim()), rows: lines.slice(1).map(parseLine) };
};
const guessMap = (headers) => {
  const lower = headers.map((h) => h.trim().toLowerCase());
  const map = {};
  for (const f of CSV_FIELDS) { const al = CSV_ALIASES[f.key] || [f.key]; map[f.key] = lower.findIndex((h) => al.includes(h)); }
  return map;
};

function ManageCarriersDialog({ open, onOpenChange, carriers, reload }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) setRows(carriers.map((c) => ({ ...c, draft: c.name }))); }, [open, carriers]);
  const save = async (c) => {
    const n = c.draft.trim();
    if (!n) return;
    try { await api.patch(`/carriers/${c.carrier_id}`, { name: n }); toast.success("Carrier renamed"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from the carrier list?`)) return;
    try { await api.delete(`/carriers/${c.carrier_id}`); toast.success("Carrier removed"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manage-carriers-dialog">
        <DialogHeader><DialogTitle className="font-heading">Manage carriers</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Rename or remove carriers from the shared dropdown. Renaming also updates existing policies.</p>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {rows.length === 0 && <p className="text-sm text-muted-foreground">No carriers yet.</p>}
          {rows.map((c, i) => (
            <div key={c.carrier_id} className="flex items-center gap-2" data-testid={`manage-carrier-${c.carrier_id}`}>
              <Input value={c.draft} onChange={(e) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, draft: e.target.value } : r))} data-testid={`carrier-rename-${c.carrier_id}`} />
              <Button size="icon" variant="outline" onClick={() => save(c)} disabled={!c.draft.trim() || c.draft.trim() === c.name} data-testid={`carrier-save-${c.carrier_id}`}><Check className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => del(c)} data-testid={`carrier-delete-${c.carrier_id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CarrierPicker({ value, onChange, carriers, onAdded }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const { data } = await api.post("/carriers", { name: n });
      toast.success(`Carrier "${data.name}" added`);
      setName(""); setAdding(false);
      onAdded(data.name);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="mt-1.5 flex items-center gap-2">
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger className="flex-1" data-testid="book-carrier-select"><SelectValue placeholder="Select a carrier" /></SelectTrigger>
          <SelectContent>
            {carriers.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No carriers yet — add one</div>}
            {carriers.map((c) => <SelectItem key={c.carrier_id} value={c.name} data-testid={`carrier-option-${c.carrier_id}`}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {!adding && <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="book-add-carrier-toggle"><Plus className="mr-1 h-4 w-4" /> New</Button>}
      </div>
      {adding && (
        <div className="mt-2 flex items-center gap-2" data-testid="book-add-carrier-row">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Carrier name (e.g. Aetna)" data-testid="book-new-carrier-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <Button type="button" size="icon" onClick={add} disabled={busy} data-testid="book-new-carrier-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}</Button>
          <Button type="button" size="icon" variant="ghost" onClick={() => { setAdding(false); setName(""); }} data-testid="book-new-carrier-cancel"><X className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

function EntryDialog({ open, onOpenChange, initial, carriers, reloadCarriers, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.entry_id;

  useEffect(() => {
    if (open) setForm(initial ? {
      carrier: initial.carrier || "", insured_name: initial.insured_name || "", address: initial.address || "",
      dob: initial.dob || "", phone: displayPhone(initial.phone), policy_number: initial.policy_number || "",
      renewal_date: initial.renewal_date || "", premium: initial.premium != null ? String(initial.premium) : "",
      members: (initial.members || []).map((m) => ({ ...m })),
    } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMember = (i, k, v) => setForm((f) => ({ ...f, members: f.members.map((m, idx) => idx === i ? { ...m, [k]: v } : m) }));
  const addMember = () => setForm((f) => ({ ...f, members: [...f.members, { name: "", dob: "" }] }));
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.insured_name.trim()) return toast.error("Insured name is required");
    setSaving(true);
    const payload = { ...form, premium: form.premium === "" ? null : Number(form.premium), members: form.members.filter((m) => m.name.trim()) };
    try {
      if (editing) await api.patch(`/book/${initial.entry_id}`, payload);
      else await api.post("/book", payload);
      toast.success(editing ? "Policy updated" : "Policy added to your book");
      onOpenChange(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit policy" : "Add a policy"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Insurance carrier</Label>
            <CarrierPicker value={form.carrier} onChange={(v) => set("carrier", v)} carriers={carriers}
              onAdded={(name) => { reloadCarriers(); set("carrier", name); }} />
          </div>
          <div>
            <Label htmlFor="insured">Insured name</Label>
            <Input id="insured" value={form.insured_name} onChange={(e) => set("insured_name", e.target.value)} className="mt-1.5" placeholder="John Smith" data-testid="book-insured-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="policy">Policy number</Label>
              <Input id="policy" value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} className="mt-1.5" placeholder="POL-000123" data-testid="book-policy-input" />
            </div>
            <div>
              <Label htmlFor="premium">Annual premium</Label>
              <Input id="premium" inputMode="numeric" value={formatCurrencyInput(form.premium)} onChange={(e) => set("premium", parseCurrencyInput(e.target.value))} className="mt-1.5" placeholder="$1,200" data-testid="book-premium-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="address">Address</Label>
            <AddressAutocomplete id="address" value={form.address} onChange={(e) => set("address", e.target.value)}
              onSelect={(a) => set("address", [a.address, a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "))}
              className="mt-1.5" placeholder="123 Main St, City, ST 00000" data-testid="book-address-input" />
          </div>
          <div>
            <Label htmlFor="address2">Address 2 <span className="text-xs text-muted-foreground">(apt, suite, unit)</span></Label>
            <Input id="address2" value={form.address2} onChange={(e) => set("address2", e.target.value)} className="mt-1.5" placeholder="Apt / Suite / Unit" data-testid="book-address2-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="dob">Date of birth</Label>
              <Input id="dob" value={form.dob} onChange={(e) => set("dob", fmtDob(e.target.value))} className="mt-1.5" placeholder="mm/dd/yyyy" maxLength={10} data-testid="book-dob-input" />
            </div>
            <div>
              <Label htmlFor="phone">Telephone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", fmtPhone(e.target.value))} className="mt-1.5" placeholder="(555) 123-4567" maxLength={14} data-testid="book-phone-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="renewal">Renewal / expiry date</Label>
            <Input id="renewal" value={form.renewal_date} onChange={(e) => set("renewal_date", fmtDob(e.target.value))} className="mt-1.5" placeholder="mm/dd/yyyy" maxLength={10} data-testid="book-renewal-input" />
            <p className="mt-1 text-xs text-muted-foreground">We'll email you a reminder up to 30 days before it lapses.</p>
          </div>

          <div className="rounded-lg border border-border p-3" data-testid="book-members-section">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><Users className="h-4 w-4" /> Other people on this policy</Label>
              <Button type="button" size="sm" variant="outline" onClick={addMember} data-testid="book-add-member"><Plus className="mr-1 h-4 w-4" /> Add person</Button>
            </div>
            {form.members.length === 0 && <p className="mt-2 text-xs text-muted-foreground">Add spouses, children or dependents covered under the insured.</p>}
            <div className="mt-3 space-y-2">
              {form.members.map((m, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`book-member-row-${i}`}>
                  <Input value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} placeholder="Full name" className="flex-1" data-testid={`book-member-name-${i}`} />
                  <Input value={m.dob} onChange={(e) => setMember(i, "dob", fmtDob(e.target.value))} placeholder="mm/dd/yyyy" maxLength={10} className="w-36" data-testid={`book-member-dob-${i}`} />
                  <Button type="button" size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeMember(i)} data-testid={`book-member-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="book-save-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Add policy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TabBtn = ({ id, view, setView, icon: Icon, children }) => (
  <button onClick={() => setView(id)} data-testid={`book-tab-${id}`}
    className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${view === id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
    <Icon className="h-4 w-4" /> {children}
  </button>
);

function PolicyCard({ e, onEdit, onDelete, onCreateLead, busyLead }) {
  return (
    <Card className="flex flex-col gap-3 p-5" data-testid={`book-entry-${e.entry_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-heading text-lg font-semibold">{e.insured_name}</p>
          {e.carrier && <Badge variant="outline" className="mt-1"><Building2 className="mr-1 h-3 w-3" /> {e.carrier}</Badge>}
        </div>
        <div className="flex shrink-0 gap-1">
          <Button size="icon" variant="ghost" onClick={() => onEdit(e)} data-testid={`book-edit-${e.entry_id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => onDelete(e)} data-testid={`book-delete-${e.entry_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        {e.policy_number && <p className="flex items-center gap-2 text-muted-foreground"><Hash className="h-3.5 w-3.5" /> {e.policy_number}</p>}
        {e.premium != null && <p className="flex items-center gap-2 text-muted-foreground"><DollarSign className="h-3.5 w-3.5" /> {money(e.premium)} / yr</p>}
        {e.dob && <p className="flex items-center gap-2 text-muted-foreground"><Cake className="h-3.5 w-3.5" /> DOB {e.dob}</p>}
        {e.phone && <a href={`tel:${e.phone}`} className="flex items-center gap-2 font-medium text-primary hover:underline" data-testid={`book-phone-${e.entry_id}`}><Phone className="h-3.5 w-3.5" /> {displayPhone(e.phone)}</a>}
        {e.address && <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {e.address}</p>}
        {e.renewal_date && <p className="flex items-center gap-2 text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" /> Renews {e.renewal_date}</p>}
      </div>
      {e.members?.length > 0 && (
        <div className="rounded-lg border border-border p-2.5" data-testid={`book-members-${e.entry_id}`}>
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Users className="h-3.5 w-3.5" /> Also covered ({e.members.length})</p>
          <div className="mt-1.5 space-y-1">
            {e.members.map((m, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span>{m.name}</span>
                {m.dob && <span className="text-xs text-muted-foreground">{m.dob}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <Button variant="outline" size="sm" className="mt-auto" disabled={busyLead === e.entry_id} onClick={() => onCreateLead(e)} data-testid={`book-create-lead-${e.entry_id}`}>
        {busyLead === e.entry_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />} Create lead
      </Button>
    </Card>
  );
}

export default function BookOfBusiness() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const [view, setView] = useState("policies");
  const [analytics, setAnalytics] = useState(null);
  const [busyLead, setBusyLead] = useState(null);
  const [importData, setImportData] = useState(null);
  const [importMap, setImportMap] = useState({});
  const [importing, setImporting] = useState(false);
  const [trends, setTrends] = useState([]);
  const fileRef = useRef();
  const isAdmin = user.role === "admin";

  const download = async (fmt) => {
    try {
      const res = await api.get(`/book/export/${fmt}`, { responseType: "blob" });
      downloadBlob(res.data, `book-of-business.${fmt}`, fmt === "pdf" ? "application/pdf" : "text/csv");
    } catch { toast.error("Export failed — please try again"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/book"); setEntries(data); } catch {}
    finally { setLoading(false); }
  }, []);
  const reloadCarriers = useCallback(() => api.get("/carriers").then(({ data }) => setCarriers(data)).catch(() => {}), []);
  const loadAnalytics = useCallback(() => api.get("/book/analytics").then(({ data }) => setAnalytics(data)).catch(() => {}), []);
  const loadTrends = useCallback(() => api.get("/book/premium-trends").then(({ data }) => setTrends(data)).catch(() => {}), []);
  useEffect(() => { load(); reloadCarriers(); }, [load, reloadCarriers]);
  useEffect(() => { if (view === "analytics") { loadAnalytics(); loadTrends(); } }, [view, entries, loadAnalytics, loadTrends]);

  const openAdd = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setDialogOpen(true); };
  const del = async (e) => {
    if (!window.confirm(`Remove ${e.insured_name} from your book?`)) return;
    try { await api.delete(`/book/${e.entry_id}`); toast.success("Policy removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const createLead = async (e) => {
    setBusyLead(e.entry_id);
    try { await api.post(`/book/${e.entry_id}/create-lead`); toast.success(`Lead created for ${e.insured_name} — check your Leads pipeline`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyLead(null); }
  };
  const onImport = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const { headers, rows } = splitCSV(text);
      if (!headers.length || !rows.length) { toast.error("Empty or invalid CSV"); return; }
      setImportData({ headers, rows });
      setImportMap(guessMap(headers));
    } catch { toast.error("Could not read the file"); }
    finally { ev.target.value = ""; }
  };
  const doImport = async () => {
    const { headers, rows } = importData;
    if (importMap.insured_name == null || importMap.insured_name < 0) return toast.error("Map the Insured name column");
    const parsed = rows.map((cells) => {
      const g = (k) => (importMap[k] >= 0 ? (cells[importMap[k]] || "").trim() : "");
      const prem = g("premium").replace(/[^0-9.]/g, "");
      return { insured_name: g("insured_name"), carrier: g("carrier"), policy_number: g("policy_number"), dob: g("dob"), phone: g("phone"), address: g("address"), renewal_date: g("renewal_date"), premium: prem ? Number(prem) : null };
    }).filter((r) => r.insured_name);
    if (!parsed.length) return toast.error("No rows with an insured name");
    setImporting(true);
    try {
      const { data } = await api.post("/book/import", { rows: parsed });
      toast.success(`Imported ${data.imported} policies`);
      setImportData(null); load(); reloadCarriers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const filtered = entries.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [e.insured_name, e.carrier, e.policy_number, e.address].some((v) => (v || "").toLowerCase().includes(s));
  });

  const buckets = [30, 60, 90].map((limit, i, arr) => {
    const lower = i === 0 ? 0 : arr[i - 1];
    return { limit, list: entries.filter((e) => { const d = daysUntil(e.renewal_date); return d != null && d >= 0 && d > lower && d <= limit; }) };
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Book of Business</h1>
          <p className="mt-1 text-sm text-muted-foreground">Your active policies, insured contacts and everyone covered under them.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="book-manage-carriers-button"><Building2 className="mr-2 h-4 w-4" /> Carriers</Button>}
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="book-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="book-import-button"><Upload className="mr-2 h-4 w-4" /> Import</Button>
          <Button variant="outline" onClick={() => download("csv")} data-testid="book-export-csv"><Download className="mr-2 h-4 w-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download("pdf")} data-testid="book-export-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</Button>
          <Button onClick={openAdd} data-testid="book-add-policy-button"><Plus className="mr-2 h-4 w-4" /> Add policy</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
        <TabBtn id="policies" view={view} setView={setView} icon={ListChecks}>Policies</TabBtn>
        <TabBtn id="renewals" view={view} setView={setView} icon={CalendarClock}>Renewals</TabBtn>
        <TabBtn id="analytics" view={view} setView={setView} icon={BarChart3}>Carrier analytics</TabBtn>
      </div>

      {view === "policies" && (
        <>
          <Card className="p-4">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by insured, carrier, policy # or address…" data-testid="book-search" />
          </Card>
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <Card className="flex flex-col items-center gap-3 py-16 text-center" data-testid="book-empty">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15"><BookMarked className="h-7 w-7 text-primary" /></div>
              <p className="font-heading text-lg font-semibold">{entries.length === 0 ? "Your book is empty" : "No matches"}</p>
              <p className="max-w-sm text-sm text-muted-foreground">{entries.length === 0 ? "Add a policy or import your existing book from a CSV to get started." : "Try a different search."}</p>
              {entries.length === 0 && <div className="flex gap-2"><Button onClick={openAdd} data-testid="book-empty-add"><Plus className="mr-2 h-4 w-4" /> Add policy</Button><Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="book-empty-import"><Upload className="mr-2 h-4 w-4" /> Import CSV</Button></div>}
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="book-list">
              {filtered.map((e) => <PolicyCard key={e.entry_id} e={e} onEdit={openEdit} onDelete={del} onCreateLead={createLead} busyLead={busyLead} />)}
            </div>
          )}
        </>
      )}

      {view === "renewals" && (
        <div className="space-y-6" data-testid="book-renewals">
          {buckets.every((b) => b.list.length === 0) && (
            <Card className="py-16 text-center text-muted-foreground" data-testid="renewals-empty">No policies renewing in the next 90 days. Add renewal dates to your policies to see them here.</Card>
          )}
          {buckets.map((b) => b.list.length > 0 && (
            <div key={b.limit} data-testid={`renewals-bucket-${b.limit}`}>
              <h3 className="mb-3 flex items-center gap-2 font-heading font-semibold"><CalendarClock className="h-4 w-4 text-primary" /> Due within {b.limit} days <Badge variant="outline">{b.list.length}</Badge></h3>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {b.list.map((e) => <PolicyCard key={e.entry_id} e={e} onEdit={openEdit} onDelete={del} onCreateLead={createLead} busyLead={busyLead} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "analytics" && (
        <div className="space-y-4" data-testid="book-analytics">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Card className="p-4"><p className="text-xs text-muted-foreground">Policies</p><p className="font-heading text-2xl font-bold" data-testid="analytics-total-policies">{analytics?.total_policies ?? 0}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Total annual premium</p><p className="font-heading text-2xl font-bold" data-testid="analytics-total-premium">{money(analytics?.total_premium)}</p></Card>
            <Card className="p-4"><p className="text-xs text-muted-foreground">Carriers</p><p className="font-heading text-2xl font-bold">{analytics?.by_carrier?.length ?? 0}</p></Card>
          </div>
          <Card className="p-5">
            <h3 className="mb-4 font-heading font-semibold">By carrier</h3>
            {(!analytics || analytics.by_carrier.length === 0) ? (
              <p className="text-sm text-muted-foreground">No policies yet.</p>
            ) : (
              <div className="space-y-3">
                {analytics.by_carrier.map((r) => {
                  const max = Math.max(...analytics.by_carrier.map((x) => x.premium), 1);
                  return (
                    <div key={r.carrier} data-testid={`analytics-carrier-${r.carrier}`}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="font-medium">{r.carrier}</span>
                        <span className="text-muted-foreground">{r.policies} {r.policies === 1 ? "policy" : "policies"} · {money(r.premium)}/yr</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(4, (r.premium / max) * 100)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          <Card className="p-5" data-testid="premium-trends">
            <h3 className="mb-4 font-heading font-semibold">Book premium — last 12 months</h3>
            {trends.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (() => {
              const max = Math.max(...trends.map((t) => t.premium), 1);
              return (
                <div className="flex h-40 items-end gap-1.5">
                  {trends.map((t) => (
                    <div key={t.month} className="flex flex-1 flex-col items-center gap-1" data-testid={`trend-${t.month}`} title={`${money(t.premium)} · ${t.policies} policies`}>
                      <div className="w-full rounded-t bg-primary/80" style={{ height: `${Math.max(2, (t.premium / max) * 140)}px` }} />
                      <span className="text-[9px] text-muted-foreground">{t.month.slice(5)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      <EntryDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} carriers={carriers} reloadCarriers={reloadCarriers} onSaved={load} />
      <ManageCarriersDialog open={manageOpen} onOpenChange={setManageOpen} carriers={carriers} reload={reloadCarriers} />

      <Dialog open={!!importData} onOpenChange={(o) => { if (!o) setImportData(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="import-mapping-dialog">
          <DialogHeader><DialogTitle className="font-heading">Map your CSV columns</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{importData?.rows.length} rows detected. Match each field to a column from your file.</p>
          <div className="space-y-3">
            {CSV_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3">
                <Label className="w-40 shrink-0">{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                <Select value={String(importMap[f.key] ?? -1)} onValueChange={(v) => setImportMap((m) => ({ ...m, [f.key]: Number(v) }))}>
                  <SelectTrigger className="flex-1" data-testid={`import-map-${f.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-1">— Skip —</SelectItem>
                    {importData?.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Column ${i + 1}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportData(null)} data-testid="import-cancel">Cancel</Button>
            <Button onClick={doImport} disabled={importing} data-testid="import-confirm">{importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Import {importData?.rows.length} rows</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
